/**
 * activityTracker
 *
 * Cliente ligero para registrar eventos de actividad del vendedor:
 *   - LOGIN / LOGOUT / APP_OPEN / APP_CLOSE
 *   - PAGE_VIEW
 *   - LEAD_MODAL_OPEN / LEAD_MODAL_CLOSE  (con duración)
 *   - SIMULATOR_OPEN / SIMULATOR_CLOSE    (con duración)
 *   - IDLE_START / IDLE_END               (umbral 2 min)
 *   - HEARTBEAT cada 60s mientras el usuario esté activo
 *
 * Los eventos se acumulan en memoria y se flushean en batch cada 60s al
 * Lambda c_logWorkerActivity (POST /activity/track). En `beforeunload` se
 * envía un flush final con `navigator.sendBeacon` para no perder eventos.
 */

import axiosClient from "../api/axiosClient";

const FLUSH_INTERVAL_MS = 60_000;
const HEARTBEAT_INTERVAL_MS = 60_000;
const IDLE_THRESHOLD_MS = 5 * 60_000;
// Mínimo de duración para registrar un par IDLE_START/IDLE_END retroactivo
// al volver de una pestaña oculta. Evita ruido de cambios breves.
const HIDDEN_IDLE_MIN_MS = IDLE_THRESHOLD_MS;
const MAX_BUFFER = 200;
const TRACK_PATH = "/activity/track";
const ACTIVITY_EVENTS = ["mousemove", "click", "keydown", "scroll", "touchstart"];

const state = {
  initialized: false,
  enabled: true,
  user_id: null,
  company_id: null,
  session_id: null,
  buffer: [],
  timers: {}, // label -> ts_ms inicio
  lastActivityMs: 0,
  isIdle: false,
  idleStartMs: 0,
  hiddenAtMs: 0,
  flushTimer: null,
  heartbeatTimer: null,
  idleCheckTimer: null,
  listenersAttached: false,
};

function rand() {
  return Math.random().toString(36).slice(2, 10);
}

function nowMs() {
  return Date.now();
}

function enqueue(event_type, metadata = {}) {
  if (!state.initialized || !state.enabled) return;
  if (state.buffer.length >= MAX_BUFFER) {
    state.buffer.shift(); // descarta el más antiguo
  }
  state.buffer.push({
    event_type,
    ts_ms: nowMs(),
    event_id: rand(),
    session_id: state.session_id,
    metadata,
  });
}

async function flush() {
  if (!state.initialized) return;
  if (state.buffer.length === 0) return;
  const events = state.buffer.splice(0, state.buffer.length);
  try {
    await axiosClient.post(TRACK_PATH, { events });
  } catch (err) {
    // Si falla, reinserta los eventos al principio (sin pasarse del límite).
    const restored = [...events, ...state.buffer].slice(0, MAX_BUFFER);
    state.buffer = restored;
    // No log spam: silencioso en consola, ya hay interceptor global.
  }
}

function flushBeacon() {
  if (!state.initialized || state.buffer.length === 0) return;
  const url = `${process.env.REACT_APP_BACKEND_URL || ""}${TRACK_PATH}`;
  const token = localStorage.getItem("token");
  if (!url || !token || !navigator.sendBeacon) return;
  try {
    // Authorization no se puede setear con sendBeacon, así que pasamos el token
    // en el body. El Lambda igualmente exige el header; como fallback aceptable,
    // usamos fetch con keepalive si sendBeacon no llega.
    const body = JSON.stringify({ events: state.buffer });
    const ok = navigator.sendBeacon(
      url,
      new Blob([body], { type: "application/json" }),
    );
    if (!ok) {
      // fetch keepalive como fallback
      fetch(url, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body,
      }).catch(() => {});
    } else {
      // Reintentar por fetch para garantizar Authorization correcta
      fetch(url, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body,
      }).catch(() => {});
    }
    state.buffer = [];
  } catch (_) {
    // noop
  }
}

function onActivity() {
  state.lastActivityMs = nowMs();
  if (state.isIdle) {
    const duration_ms = nowMs() - state.idleStartMs;
    state.isIdle = false;
    // Solo registramos IDLE_END si la inactividad realmente duró el umbral.
    // Esto protege frente a estados inconsistentes (no debería pasar con
    // checkIdle, pero evita ruido si algún flujo manipula state).
    if (duration_ms >= IDLE_THRESHOLD_MS) {
      enqueue("IDLE_END", { duration_ms });
    }
  }
}

function checkIdle() {
  if (!state.initialized) return;
  if (state.isIdle) return;
  const delta = nowMs() - state.lastActivityMs;
  if (delta >= IDLE_THRESHOLD_MS) {
    state.isIdle = true;
    state.idleStartMs = state.lastActivityMs;
    enqueue("IDLE_START", {});
  }
}

function heartbeat() {
  if (!state.initialized) return;
  if (state.isIdle) return;
  enqueue("HEARTBEAT", { path: window.location?.pathname || "" });
}

function attachGlobalListeners() {
  if (state.listenersAttached) return;
  state.listenersAttached = true;
  ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
  window.addEventListener("beforeunload", () => {
    enqueue("APP_CLOSE", {});
    flushBeacon();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      // No emitimos IDLE_START aquí: cambios breves de pestaña generaban
      // pares "Inactivo 0s / Vuelve 1s" en cascada. Solo registramos el
      // instante en que se ocultó la pestaña para evaluarlo al volver.
      state.hiddenAtMs = nowMs();
    } else {
      const wasHiddenForMs = state.hiddenAtMs ? nowMs() - state.hiddenAtMs : 0;
      state.hiddenAtMs = 0;
      // Si la pestaña estuvo oculta el tiempo suficiente, registramos el
      // intervalo como una inactividad única (par START+END retroactivo).
      if (wasHiddenForMs >= HIDDEN_IDLE_MIN_MS && !state.isIdle) {
        // Insertamos los eventos con timestamps consistentes haciendo que el
        // backend reciba un IDLE_START primero y un IDLE_END inmediato con
        // la duración real medida.
        enqueue("IDLE_START", { reason: "hidden" });
        enqueue("IDLE_END", { duration_ms: wasHiddenForMs, reason: "hidden" });
      }
      onActivity();
    }
  });
}

function startTimers() {
  if (state.flushTimer) clearInterval(state.flushTimer);
  if (state.heartbeatTimer) clearInterval(state.heartbeatTimer);
  if (state.idleCheckTimer) clearInterval(state.idleCheckTimer);
  state.flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
  state.heartbeatTimer = setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);
  state.idleCheckTimer = setInterval(checkIdle, 30_000);
}

const activityTracker = {
  init({ user_id, company_id }) {
    if (state.initialized && state.user_id === user_id) return;
    state.user_id = user_id || null;
    state.company_id = company_id || null;
    state.session_id = rand();
    state.lastActivityMs = nowMs();
    state.isIdle = false;
    state.idleStartMs = 0;
    state.initialized = true;
    state.enabled = true;
    attachGlobalListeners();
    startTimers();
  },

  /**
   * Registra un evento. metadata es opcional.
   */
  track(event_type, metadata = {}) {
    enqueue(event_type, metadata || {});
  },

  /**
   * Marca el inicio de un timer con `label`. Útil para calcular duración
   * entre OPEN/CLOSE sin acoplar lógica al consumidor.
   */
  startTimer(label) {
    if (!label) return;
    state.timers[label] = nowMs();
  },

  /**
   * Cierra el timer y devuelve la duración en ms (0 si no había start).
   */
  endTimer(label) {
    if (!label || !state.timers[label]) return 0;
    const dur = nowMs() - state.timers[label];
    delete state.timers[label];
    return dur;
  },

  /**
   * Flush manual (no necesario en uso normal).
   */
  async flush() {
    return flush();
  },

  /**
   * Para logout: envía LOGOUT, flushea y deshabilita el tracker.
   */
  async shutdown() {
    if (!state.initialized) return;
    enqueue("LOGOUT", {});
    state.enabled = false;
    if (state.flushTimer) clearInterval(state.flushTimer);
    if (state.heartbeatTimer) clearInterval(state.heartbeatTimer);
    if (state.idleCheckTimer) clearInterval(state.idleCheckTimer);
    state.flushTimer = null;
    state.heartbeatTimer = null;
    state.idleCheckTimer = null;
    await flush();
    state.initialized = false;
    state.user_id = null;
    state.company_id = null;
    state.session_id = null;
    state.buffer = [];
  },

  isInitialized() {
    return state.initialized;
  },
};

export default activityTracker;
